/**
 * LlmHealthTab — admin one-button probe of every LLM provider/model
 * the app currently uses. Saves us from the next "Gemini retired the
 * model and we found out from a user" incident.
 *
 * Calls GET /api/admin/llm-healthcheck which fires every model in
 * parallel (~3s wall clock when all healthy, ~12s if one times out).
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, Loader2, CheckCircle2, AlertTriangle, XCircle, Zap, Clock, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  ok:      { Icon: CheckCircle2,    color: '#34C759', label: 'Healthy' },
  timeout: { Icon: Clock,           color: '#FF9F0A', label: 'Timeout' },
  error:   { Icon: XCircle,         color: '#FF453A', label: 'Error' },
};

const OVERALL_META = {
  healthy:  { color: '#34C759', label: 'All systems go' },
  degraded: { color: '#FF9F0A', label: 'Degraded — at least one model down' },
  down:     { color: '#FF453A', label: 'Critical — every provider failing' },
};

const Row = ({ result }) => {
  const meta = STATUS_META[result.status] || STATUS_META.error;
  const { Icon } = meta;
  const isSlow = result.status === 'ok' && result.latency_ms > 3000;
  return (
    <div
      className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-[var(--isp-glass-border)] hover:bg-white/[0.02] transition-colors"
      data-testid={`llm-health-row-${result.provider}-${result.model}`}
    >
      <div className="col-span-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/85 uppercase tracking-wider">
          {result.provider}
        </span>
      </div>
      <div className="col-span-3">
        <p className="text-sm font-medium text-white">{result.model}</p>
        <p className="text-xs text-white/45 mt-0.5 truncate">{result.purpose}</p>
      </div>
      <div className="col-span-2">
        <span className="inline-flex items-center gap-1.5" style={{ color: meta.color }}>
          <Icon className="w-4 h-4" />
          <span className="text-xs font-semibold">{meta.label}</span>
        </span>
      </div>
      <div className="col-span-2">
        <span className={`text-xs font-mono ${isSlow ? 'text-orange-400' : 'text-white/65'}`}>
          {result.latency_ms} ms{isSlow && ' ⚠'}
        </span>
      </div>
      <div className="col-span-3">
        {result.status === 'ok' ? (
          <span className="text-xs text-white/45 font-mono truncate block" title={result.reply_snippet}>
            ↳ {result.reply_snippet || '—'}
          </span>
        ) : (
          <span className="text-xs text-red-400 font-mono truncate block" title={result.error}>
            {result.error || '—'}
          </span>
        )}
      </div>
    </div>
  );
};

export const LlmHealthTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/llm-healthcheck`);
      setData(res.data);
      const { ok_count, total_count } = res.data;
      if (ok_count === total_count) toast.success(`All ${total_count} LLM models healthy`);
      else if (ok_count === 0) toast.error('All LLM providers failing — check logs immediately');
      else toast.warning(`${total_count - ok_count} of ${total_count} models failing`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Health check failed';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const overall = data ? (OVERALL_META[data.overall] || OVERALL_META.down) : null;
  const ranAt = data ? new Date(data.ran_at * 1000).toLocaleString() : null;

  return (
    <div className="space-y-6" data-testid="llm-health-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Zap className="w-4 h-4 text-purple-400" />
            LLM Provider Health Check
          </h2>
          <p className="text-sm text-white/55 mt-1 max-w-2xl">
            Fires a real chat probe against every model the app uses (Gemini, OpenAI, Claude — via Emergent universal key). Catches model deprecations &amp; rate-limit issues within seconds, before users see "trouble connecting" errors. Probes run in parallel; expect 2–8 seconds wall-clock when healthy.
          </p>
        </div>
        <button
          onClick={run}
          disabled={loading}
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] hover:bg-[#1A47C2] disabled:bg-white/10 disabled:cursor-not-allowed text-white font-semibold text-xs tracking-tight transition-colors"
          data-testid="llm-health-run-btn"
        >
          {loading ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Probing…</>) : (<><RefreshCw className="w-3.5 h-3.5" /> Run health check</>)}
        </button>
      </div>

      {/* Overall card */}
      {data && (
        <div
          className="border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl p-5 flex items-center justify-between"
          data-testid="llm-health-overall"
          style={{ borderLeftWidth: 4, borderLeftColor: overall.color }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">Overall</p>
            <p className="text-xl font-bold text-white" style={{ color: overall.color }} data-testid="llm-health-overall-label">
              {overall.label}
            </p>
            <p className="text-xs text-white/45 mt-1">
              {data.ok_count} / {data.total_count} models healthy · ran at {ranAt} · total wall-clock {data.total_ms} ms
            </p>
          </div>
          <Activity className="w-10 h-10" style={{ color: overall.color, opacity: 0.4 }} />
        </div>
      )}

      {/* Empty state */}
      {!data && !loading && (
        <div className="border border-dashed border-[var(--isp-glass-border)] bg-slate-900/30 p-10 text-center" data-testid="llm-health-empty">
          <Activity className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-sm text-white/55">Click <strong className="text-white">Run health check</strong> above to probe each LLM model.</p>
          <p className="text-xs text-white/35 mt-1">Recommended cadence: weekly, or any time a user reports "AI not responding".</p>
        </div>
      )}

      {/* Results table */}
      {data && (
        <div className="border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl overflow-hidden" data-testid="llm-health-results">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-white/[0.04] border-b border-[var(--isp-glass-border)]">
            <div className="col-span-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">Provider</div>
            <div className="col-span-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">Model · purpose</div>
            <div className="col-span-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">Status</div>
            <div className="col-span-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">Latency</div>
            <div className="col-span-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">Reply / Error</div>
          </div>
          {data.results.map((r) => (
            <Row key={`${r.provider}-${r.model}`} result={r} />
          ))}
        </div>
      )}

      {/* Failure-mode hint when degraded */}
      {data && data.overall !== 'healthy' && (
        <div className="border border-yellow-500/20 bg-yellow-500/5 p-4 flex items-start gap-3" data-testid="llm-health-hint">
          <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
          <div className="text-xs text-white/75 leading-relaxed">
            <p className="font-semibold text-yellow-300 mb-1">Likely causes if a row shows "Error":</p>
            <ul className="list-disc ml-4 space-y-0.5">
              <li><strong>"no longer available"</strong> — provider retired the model. Update <code className="text-white/85">.with_model(...)</code> in <code className="text-white/85">/backend/routers/</code> to the next supported version.</li>
              <li><strong>"401" / "invalid API key"</strong> — <code className="text-white/85">EMERGENT_LLM_KEY</code> env var missing or expired. Top up balance in Profile.</li>
              <li><strong>"429" / "rate limit"</strong> — temporary spike. Re-run in 60s.</li>
              <li><strong>Timeout</strong> — provider slow but not down. Often clears within minutes.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default LlmHealthTab;
